import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const RegisterSchema = z.object({
  code: z.string().trim().min(4).max(32),
  deviceId: z.string().trim().max(64).optional().nullable(),
});

/**
 * Called by client right after a successful signup (auth user created).
 * Looks up the referrer by referral_code, then inserts a referrals row.
 * Idempotent: returns ok=false silently for self-referral, duplicate, etc.
 */
export const registerReferralAfterSignup = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => RegisterSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const code = data.code.toUpperCase().trim();

    // Find referrer by referral_code (case-insensitive search but stored UPPER).
    const { data: referrerProfile, error: lookupErr } = await supabaseAdmin
      .from("profiles")
      .select("user_id, banned")
      .ilike("referral_code", code)
      .maybeSingle();

    if (lookupErr) {
      console.error("[referrals] lookup error:", lookupErr);
      return { ok: false, reason: "lookup_failed" as const };
    }

    if (!referrerProfile) {
      return { ok: false, reason: "invalid_code" as const };
    }

    if (referrerProfile.user_id === userId) {
      return { ok: false, reason: "self_referral" as const };
    }

    if (referrerProfile.banned) {
      return { ok: false, reason: "referrer_banned" as const };
    }

    // Check duplicate (referred_id has UNIQUE index)
    const { data: existing } = await supabaseAdmin
      .from("referrals")
      .select("id")
      .eq("referred_id", userId)
      .maybeSingle();

    if (existing) {
      return { ok: false, reason: "already_referred" as const };
    }

    const { error: insertErr } = await supabaseAdmin.from("referrals").insert({
      referrer_id: referrerProfile.user_id,
      referred_id: userId,
      code,
      reward_credits: 50,
      claimed: false,
      device_id: data.deviceId ?? null,
    });

    if (insertErr) {
      console.error("[referrals] insert error:", insertErr);
      // Unique-violation race → treat as already_referred
      if ((insertErr as { code?: string }).code === "23505") {
        return { ok: false, reason: "already_referred" as const };
      }
      return { ok: false, reason: "insert_failed" as const };
    }

    // Update profile.referred_by_user_id
    await supabaseAdmin
      .from("profiles")
      .update({ referred_by_user_id: referrerProfile.user_id })
      .eq("user_id", userId);

    return { ok: true as const, referrerId: referrerProfile.user_id };
  });

/**
 * Returns the current user's referral page data:
 * - their referral_code
 * - stats: invited count, claimed count, pending count, tickets earned
 * - history list (last 50)
 */
export const getMyReferralOverview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: profile } = await supabase
      .from("profiles")
      .select("referral_code")
      .eq("user_id", userId)
      .maybeSingle();

    const { data: referrals } = await supabase
      .from("referrals")
      .select("id, referred_id, claimed, claimed_at, reward_credits, created_at, fraud_flag")
      .eq("referrer_id", userId)
      .order("created_at", { ascending: false })
      .limit(50);

    const list = referrals ?? [];

    // Resolve display names of referred users (admin client OK: only display_name)
    const referredIds = list.map((r) => r.referred_id);
    let nameMap: Record<string, string | null> = {};
    if (referredIds.length > 0) {
      const { data: names } = await supabaseAdmin
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", referredIds);
      nameMap = Object.fromEntries(
        (names ?? []).map((n) => [n.user_id, n.display_name ?? null]),
      );
    }

    const claimedCount = list.filter((r) => r.claimed).length;
    const pendingCount = list.filter((r) => !r.claimed && !r.fraud_flag).length;
    const ticketsEarned = list
      .filter((r) => r.claimed)
      .reduce((sum, r) => sum + (r.reward_credits ?? 0), 0);

    return {
      referralCode: profile?.referral_code ?? null,
      stats: {
        invitedCount: list.length,
        claimedCount,
        pendingCount,
        ticketsEarned,
      },
      history: list.map((r) => {
        const name = nameMap[r.referred_id];
        // Privacy: mask display_name
        const masked = name
          ? name.length <= 1
            ? `${name}***さん`
            : `${name.slice(0, 1)}***さん`
          : "***さん";
        return {
          id: r.id,
          createdAt: r.created_at,
          referredDisplayName: masked,
          status: r.fraud_flag
            ? ("review" as const)
            : r.claimed
              ? ("claimed" as const)
              : ("pending" as const),
          rewardCredits: r.reward_credits ?? 0,
          claimedAt: r.claimed_at,
        };
      }),
    };
  });
